"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.validateComponent = validateComponent;
exports.validateComponentEvents = validateComponentEvents;
var _regenerator = _interopRequireDefault(require("@babel/runtime/regenerator"));
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireDefault(require("react"));
var _react2 = require("@testing-library/react");
var _userEvent = _interopRequireDefault(require("@testing-library/user-event"));
/* eslint-disable import/no-extraneous-dependencies */

var customTestId = 'custom-test-id';

/**
 * Validates a component's test IDs and inner elements rendering
 *
 * @example
 * ```tsx
 * // Basic usage with default test ID
 * validateComponent({
 *   Component: Button,
 *   props: { children: 'Click me' },
 *   defaultTestId: 'button'
 * });
 *
 * // Testing inner elements
 * validateComponent({
 *   Component: ListItem,
 *   props: {
 *     prefix: <Icon />,
 *     suffix: <Icon />,
 *     children: 'List Item'
 *   },
 *   defaultTestId: 'list-item',
 *   innerElements: ['prefix', 'suffix']
 * });
 *
 * // Skip custom test ID validation
 * validateComponent({
 *   Component: Text,
 *   props: { children: 'Hello' },
 *   defaultTestId: 'text',
 *   skipCustomTestId: true
 * });
 * ```
 *
 * // Custom disabled attribute
 * validateComponent({
 *   Component: Button,
 *   props: { children: 'Click me' },
 *   defaultTestId: 'button',
 *   disabledAttribute: 'aria-disabled'
 * });
 */
function validateComponent(config) {
  var Component = config.Component,
    props = config.props,
    _config$defaultTestId = config.defaultTestId,
    defaultTestId = _config$defaultTestId === void 0 ? 'component' : _config$defaultTestId,
    _config$innerElements = config.innerElements,
    innerElements = _config$innerElements === void 0 ? [] : _config$innerElements,
    _config$skipCustomTes = config.skipCustomTestId,
    skipCustomTestId = _config$skipCustomTes === void 0 ? false : _config$skipCustomTes,
    _config$skipForwardRe = config.skipForwardRef,
    skipForwardRef = _config$skipForwardRe === void 0 ? false : _config$skipForwardRe,
    _config$disabledAttri = config.disabledAttribute,
    disabledAttribute = _config$disabledAttri === void 0 ? 'data-disabled' : _config$disabledAttri;
  describe('Component validation', function () {
    describe('Test IDs', function () {
      it('should render with default test ID', function () {
        (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, props));
        expect(_react2.screen.getByTestId(defaultTestId)).toBeInTheDocument();
      });
      if (!skipCustomTestId) {
        it('should render with custom test ID', function () {
          (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
            "data-testid": customTestId
          })));
          expect(_react2.screen.getByTestId(customTestId)).toBeInTheDocument();
        });
      }
    });
    if (innerElements.length > 0) {
      describe('Inner elements', function () {
        if (defaultTestId) {
          innerElements.forEach(function (element) {
            it("should render ".concat(element, " element with default test ID"), function () {
              (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, props));
              var elementTestId = "".concat(defaultTestId, "-").concat(element);
              expect(_react2.screen.getByTestId(elementTestId)).toBeInTheDocument();
            });
          });
        }
        if (!skipCustomTestId) {
          innerElements.forEach(function (element) {
            it("should render ".concat(element, " element with custom test ID"), function () {
              (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
                "data-testid": customTestId
              })));
              var elementTestId = "".concat(customTestId, "-").concat(element);
              expect(_react2.screen.getByTestId(elementTestId)).toBeInTheDocument();
            });
          });
        }
      });
    }
    if (!skipForwardRef) {
      describe('ForwaredRef', function () {
        it('should forward ref to the root element', function () {
          var ref = /*#__PURE__*/_react["default"].createRef();
          (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
            ref: ref
          })));
          expect(ref.current).toBeInstanceOf(HTMLElement);
          expect(ref.current).toHaveAttribute('data-testid', defaultTestId !== null && defaultTestId !== void 0 ? defaultTestId : customTestId);
        });
      });
    }
    var hasDisabledProp = true;
    if (hasDisabledProp) {
      describe('Disabled state', function () {
        it('should be disabled when disabled prop is true', function () {
          (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, !defaultTestId && {
            'data-testid': customTestId
          }, {
            disabled: true
          })));
          expect(_react2.screen.getByTestId(defaultTestId !== null && defaultTestId !== void 0 ? defaultTestId : customTestId)).toHaveAttribute(disabledAttribute);
        });
      });
    }
  });
}
/**
 * Validates a component's event handling behavior
 *
 * Tests the following interactions:
 * - Mouse click events
 * - Keyboard interactions (Enter and Space keys)
 * - Disabled state behavior
 *
 * The validation includes:
 * 1. Click interactions:
 *    - Mouse click triggers onClick
 *    - Enter key triggers onClick
 *    - Space key triggers onClick
 *
 * 2. Disabled state (if component has disabled/isDisabled prop):
 *    - Click does not trigger onClick
 *    - Enter key does not trigger onClick
 *    - Space key does not trigger onClick
 *
 * @example
 * ```tsx
 * // Basic onClick validation with all tests
 * validateComponentEvents({
 *   Component: Button,
 *   props: { children: 'Click me' },
 *   defaultTestId: 'button'
 * });
 *
 * // Customize onClick tests
 * validateComponentEvents({
 *   Component: Button,
 *   props: { children: 'Click me' },
 *   defaultTestId: 'button',
 *   onClickConfig: {
 *     skipEnter: true,    // Skip Enter key tests
 *     skipSpace: true,    // Skip Space key tests
 *   }
 * });
 * ```
 */
function validateComponentEvents(config) {
  var Component = config.Component,
    props = config.props,
    onClickConfig = config.onClickConfig,
    defaultTestId = config.defaultTestId;
  describe('Validate events', function () {
    var hasDisabledProp = true;
    var hasOnClickProp = true;
    if (hasOnClickProp && !(onClickConfig !== null && onClickConfig !== void 0 && onClickConfig.skip)) {
      describe('onClick', function () {
        it('onClick is called when clicked', function () {
          var onClick = jest.fn();
          (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, !defaultTestId && {
            'data-testid': customTestId
          }, {
            onClick: onClick
          })));
          var component = _react2.screen.getByTestId(defaultTestId !== null && defaultTestId !== void 0 ? defaultTestId : customTestId);
          _react2.fireEvent.click(component);
          expect(onClick).toHaveBeenCalled();
        });
        if (!(onClickConfig !== null && onClickConfig !== void 0 && onClickConfig.skipEnter)) {
          it('onClick is called when Enter key is pressed', /*#__PURE__*/(0, _asyncToGenerator2["default"])(/*#__PURE__*/_regenerator["default"].mark(function _callee() {
            var onClick;
            return _regenerator["default"].wrap(function _callee$(_context) {
              while (1) switch (_context.prev = _context.next) {
                case 0:
                  onClick = jest.fn();
                  (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
                    onClick: onClick
                  })));
                  // Tab to the component
                  _context.next = 4;
                  return _userEvent["default"].tab();
                case 4:
                  _context.next = 6;
                  return _userEvent["default"].keyboard('{Enter}');
                case 6:
                  expect(onClick).toHaveBeenCalled();
                case 7:
                case "end":
                  return _context.stop();
              }
            }, _callee);
          })));
        }
        if (!(onClickConfig !== null && onClickConfig !== void 0 && onClickConfig.skipEnter)) {
          it('onClick is called when Space key is pressed', /*#__PURE__*/(0, _asyncToGenerator2["default"])(/*#__PURE__*/_regenerator["default"].mark(function _callee2() {
            var onClick;
            return _regenerator["default"].wrap(function _callee2$(_context2) {
              while (1) switch (_context2.prev = _context2.next) {
                case 0:
                  onClick = jest.fn();
                  (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
                    onClick: onClick
                  })));
                  // Tab to the component
                  _context2.next = 4;
                  return _userEvent["default"].tab();
                case 4:
                  _context2.next = 6;
                  return _userEvent["default"].keyboard(' ');
                case 6:
                  expect(onClick).toHaveBeenCalled();
                case 7:
                case "end":
                  return _context2.stop();
              }
            }, _callee2);
          })));
        }
        if (hasDisabledProp) {
          describe('disabled state', function () {
            it('onClick is not called when clicked in disabled state', function () {
              var onClick = jest.fn();
              (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
                onClick: onClick
              }, !defaultTestId && {
                'data-testid': customTestId
              }, {
                disabled: true
              })));
              var component = _react2.screen.getByTestId(defaultTestId !== null && defaultTestId !== void 0 ? defaultTestId : customTestId);
              _react2.fireEvent.click(component);
              expect(onClick).not.toHaveBeenCalled();
            });
            if (!(onClickConfig !== null && onClickConfig !== void 0 && onClickConfig.skipEnter)) {
              it('onClick is not called when Enter key is pressed in disabled state', /*#__PURE__*/(0, _asyncToGenerator2["default"])(/*#__PURE__*/_regenerator["default"].mark(function _callee3() {
                var onClick;
                return _regenerator["default"].wrap(function _callee3$(_context3) {
                  while (1) switch (_context3.prev = _context3.next) {
                    case 0:
                      onClick = jest.fn();
                      (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
                        onClick: onClick
                      }, !defaultTestId && {
                        'data-testid': customTestId
                      }, {
                        disabled: true
                      })));
                      // Tab to the component
                      _context3.next = 4;
                      return _userEvent["default"].tab();
                    case 4:
                      _context3.next = 6;
                      return _userEvent["default"].keyboard('{Enter}');
                    case 6:
                      expect(onClick).not.toHaveBeenCalled();
                    case 7:
                    case "end":
                      return _context3.stop();
                  }
                }, _callee3);
              })));
            }
            if (!(onClickConfig !== null && onClickConfig !== void 0 && onClickConfig.skipSpace)) {
              it('onClick is not called when Space key is pressed in disabled state', /*#__PURE__*/(0, _asyncToGenerator2["default"])(/*#__PURE__*/_regenerator["default"].mark(function _callee4() {
                var onClick;
                return _regenerator["default"].wrap(function _callee4$(_context4) {
                  while (1) switch (_context4.prev = _context4.next) {
                    case 0:
                      onClick = jest.fn();
                      (0, _react2.render)(/*#__PURE__*/_react["default"].createElement(Component, (0, _extends2["default"])({}, props, {
                        onClick: onClick
                      }, !defaultTestId && {
                        'data-testid': customTestId
                      }, {
                        disabled: true
                      })));
                      // Tab to the component
                      _context4.next = 4;
                      return _userEvent["default"].tab();
                    case 4:
                      _context4.next = 6;
                      return _userEvent["default"].keyboard(' ');
                    case 6:
                      expect(onClick).not.toHaveBeenCalled();
                    case 7:
                    case "end":
                      return _context4.stop();
                  }
                }, _callee4);
              })));
            }
          });
        }
      });
    }

    // TODO: add more events here
  });
}
//# sourceMappingURL=validate.js.map